/**
 * Crazy Cribbage - UIController
 * Presentation layer controller for turn prompts, street banners, toasts,
 * count glows, and callout overlays.
 */

import { ChatManager } from '../net/ChatManager.js';
import { appStorage } from '../storage/IndexedDBStorage.js';
import { ClashSandboxModal } from '../variants/ClashSandboxModal.js';

function getStorage(s) {
  return s || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
}

export class UIController {
  constructor(options = {}) {
    this.options = options;
  }

  /**
   * Tests if a message text corresponds to a Crazy Cribbage bonus or obstacle event.
   */
  static isBonusMessage(text) {
    if (!text) return false;
    const s = String(text);
    return /sign|plus 5|plus 10|minus 5|minus 10|wheel|dice|undo|selected|chose sign|all red|all black|all odd|all even|all diff|suicide king|king of hearts|queen of spades|ace of spades|spades cut|no spade|spades|guess starter|starter guess|starter guessed|full house|straight flush|royal flush|straight|stolen crib|crib stolen|thirty-one for five|31.*for 5|nineteen for two|19.*for 2|thirty-one|nineteen|detour|round about|jail|bar|timeout|passing lane|stop sign|partner|flush in play|flush of|double run|triple run|quadruple run|bonus/i.test(s);
  }

  /**
   * Converts score reason into classic cribbage callout phrase.
   */
  static fancyPegPhrase(why, n) {
    const w = String(why || '').toLowerCase();
    const xRunM = w.match(/(\d+)x run of (\d+)/);
    const qRunM = w.match(/quadruple run of (\d+)/);
    const tRunM = w.match(/triple run of (\d+)/);
    const dRunM = w.match(/double run of (\d+)/);
    const runM = w.match(/run of (\d+)/);
    const flushM = w.match(/flush of (\d+)/);
    if (w.includes('thirty-one') || w.includes('31')) return n === 5 ? 'Thirty-one for five' : 'Thirty-one for two';
    if (w.includes('nineteen') || w === '19') return 'Nineteen for two';
    if (w.includes('fifteen')) return 'Fifteen for two';
    if (w.includes('double pair royal')) return 'Double pair royal';
    if (w.includes('pair royal')) return 'Pair royal';
    if (w.includes('pair')) return 'Pair for two';
    if (xRunM) return xRunM[1] + 'x run of ' + xRunM[2] + (n ? ' for ' + n : '');
    if (qRunM) return 'Quadruple run of ' + qRunM[1] + (n ? ' for ' + n : '');
    if (tRunM) return 'Triple run of ' + tRunM[1] + (n ? ' for ' + n : '');
    if (dRunM) return 'Double run of ' + dRunM[1] + (n ? ' for ' + n : '');
    if (runM) return 'Run of ' + runM[1];
    if (w.includes('run')) return 'Run of ' + n;
    if (flushM) return 'Flush of ' + flushM[1];
    if (w.includes('flush')) return 'Flush of ' + n;
    if (w.includes('go')) return 'One for last';
    if (w.includes('heels')) return 'Two for his heels';
    if (w.includes('nobs')) return 'One for his nobs';
    return n ? ('+' + n + '  ' + why) : why;
  }

  /**
   * Updates count glow classes and radial clock indicator.
   */
  static updateCountGlow(count = 0, phase = 'play', elements = null) {
    if (typeof document === 'undefined') return;

    const chip = (elements && elements.chip) || document.querySelector('.count-chip');
    const clock = (elements && elements.clock) || document.getElementById('countClock');
    const pile = (elements && elements.pile) || document.querySelector('.play-pile');

    if (chip) chip.classList.remove('hit-15', 'hit-31');
    if (clock) clock.classList.remove('hit-15', 'hit-31');
    if (pile) pile.classList.remove('hit-15', 'hit-31');

    const n = count || 0;
    if (clock) {
      clock.style.setProperty('--p', String(Math.max(0, Math.min(100, (n / 31) * 100))));
    }

    if (phase !== 'play') return;

    const hit = n === 31 ? 'hit-31' : (n === 15 ? 'hit-15' : '');
    if (hit) {
      if (chip) chip.classList.add(hit);
      if (clock) clock.classList.add(hit);
      if (pile) pile.classList.add(hit);
    }
  }

  /**
   * Displays milestone street announcement banner.
   */
  static showStreetBanner(who, mark, options = {}) {
    if (typeof document === 'undefined') return;

    const pName = options.pName || (typeof window !== 'undefined' && window.pName) || (w => w);
    const streetName = options.streetName || (typeof window !== 'undefined' && window.streetName) || (m => 'Street ' + m);
    const fancyCall = options.fancyCall || (typeof window !== 'undefined' && window.fancyCall);

    const el = document.getElementById('streetBanner');
    if (!el) {
      if (typeof fancyCall === 'function') {
        fancyCall(pName(who) + ' — ' + streetName(mark), 'street', 1600);
      }
      return;
    }

    el.innerHTML = `<span class="who">${pName(who)}</span>${streetName(mark)}`;
    el.className = 'on';

    clearTimeout(UIController._streetBannerTimer);
    UIController._streetBannerTimer = setTimeout(() => {
      el.className = '';
      el.textContent = '';
    }, 1800);
  }

  /**
   * Displays animated score toast notification.
   */
  static showScoreToast(who, n, why, customDuration = null, options = {}) {
    if (typeof document === 'undefined') return;

    const box = (options && options.container) || document.getElementById('toasts');
    if (!box) return;

    const pName = (options && options.pName) || (typeof window !== 'undefined' && window.pName) || (w => w);
    const isBonus = UIController.isBonusMessage(why);
    const toastDuration = customDuration || (isBonus ? 5500 : 3800);

    const el = document.createElement('div');
    el.className = 'toast ' + who + (isBonus ? ' bonus-toast' : '');
    el.style.setProperty('--toast-duration', `${Math.max(1, (toastDuration - 400) / 1000)}s`);

    const ptsText = (typeof n === 'string') ? n : (n > 0 ? `+${n}` : (n < 0 ? `${n}` : '0'));
    el.innerHTML = `<div class="who">${pName(who)}${isBonus ? ' <span class="toast-bonus-badge">BONUS</span>' : ''}</div>
      <div class="pts">${ptsText}</div>
      <div class="why">${why}</div>`;

    box.prepend(el);
    setTimeout(() => {
      if (el && el.parentNode) el.remove();
    }, toastDuration);
  }

  /**
   * Displays center screen callout text.
   */
  static fancyCall(text, kind, ms = null, options = {}) {
    if (typeof document === 'undefined') return;

    const el = (options && options.element) || document.getElementById('callout');
    if (!el || !text) return;

    const fsElem = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
    const b3dElem = (typeof document.querySelector === 'function')
      ? document.querySelector('.board-3d-container.is-fullscreen, .board-3d-container:fullscreen')
      : null;
    const targetParent = fsElem || b3dElem;
    if (targetParent && el.parentElement !== targetParent) {
      targetParent.appendChild(el);
    } else if (!targetParent && el.parentElement !== document.body && document.body) {
      document.body.appendChild(el);
    }

    const isBonus = UIController.isBonusMessage(text) || kind === 'signs' || kind === 'famous';
    const displayMs = ms || (isBonus ? 2600 : 1300);

    el.className = 'on' + (kind ? ' ' + kind : '') + (isBonus ? ' bonus-call' : '');
    el.textContent = text;

    clearTimeout(UIController._fancyCallTimer);
    UIController._fancyCallTimer = setTimeout(() => {
      el.className = '';
      el.textContent = '';
    }, displayMs);
  }

  /**
   * Updates game status prompt and state customStatus.
   */
  static setStatus(t, state = null, promptFn = null) {
    if (state) state.customStatus = t;
    if (typeof promptFn === 'function') {
      promptFn(t);
    } else if (typeof window !== 'undefined' && typeof window.updateTurnPrompt === 'function') {
      window.updateTurnPrompt(t);
    }
  }

  /**
   * Updates game status line based on turn and match phase.
   * @param {string} [overrideText]
   * @param {Object} [options]
   */
  static updateTurnPrompt(overrideText, options = {}) {
    const state = options.state || (typeof window !== 'undefined' ? window.state : null) || {};
    const settings = options.settings || (typeof window !== 'undefined' ? window.settings : null) || {};
    return UIController.formatTurnPrompt(overrideText, state, settings, options);
  }

  /**
   * Formats prompt HTML for turn status line.
   */
  static formatTurnPrompt(overrideText, state, settings, options = {}) {
    const el = (options && options.element) || (typeof document !== 'undefined' ? document.getElementById('status') : null);
    if (!state) return;

    const pName = options.pName || (typeof window !== 'undefined' && window.pName) || (s => s);
    const seatName = options.seatName || (typeof window !== 'undefined' && window.seatName) || (s => s);
    const mySeat = options.mySeat || (typeof window !== 'undefined' && window.mySeat) || (() => 'you');
    const isOnline = options.isOnline || (typeof window !== 'undefined' && window.isOnline) || (() => false);
    const isHvH = options.isHvH || (typeof window !== 'undefined' && window.isHvH) || (() => false);
    const isSeatHuman = options.isSeatHuman || (typeof window !== 'undefined' && window.isSeatHuman) || (() => true);
    const nextSeat = options.nextSeat || (typeof window !== 'undefined' && window.nextSeat) || (s => s === 'p1' ? 'p2' : 'p1');
    const cardLabel = options.cardLabel || (typeof window !== 'undefined' && window.cardLabel) || (c => c);

    const me = mySeat();
    const count = (settings && settings.playerCount) || 2;
    const is7Card = Boolean(settings && settings.opt7CardCribbage && count === 2);
    const discardN = count >= 3 ? 1 : (is7Card ? 2 : (settings && settings.deal === 'super' ? 3 : 2));

    let text = overrideText || '';
    let isMyTurn = false;
    let isWait = false;

    if (!text) {
      if (state.over) {
        text = state.winner === me ? 'Victory! You won the match!' : 'Match over. ' + pName(state.winner) + ' won!';
      } else if (state.phase === 'cut') {
        const curTurn = state.cutTurn || 'p1';
        if (isOnline()) {
          const myPick = state.cutPicks && state.cutPicks[me];
          if (myPick) {
            isWait = true;
            text = 'You cut the ' + cardLabel(myPick) + '. Waiting for ' + pName(state.cutTurn) + ' to cut the deck…';
          } else if (state.cutTurn === me) {
            isMyTurn = true;
            text = 'Your cut — tap any card in the fan to cut the deck (lowest card deals).';
          } else {
            isWait = true;
            text = 'Waiting for ' + pName(state.cutTurn) + ' to cut the deck…';
          }
        } else {
          const curPick = state.cutPicks && state.cutPicks[curTurn];
          if (!curPick && isSeatHuman(curTurn)) {
            isMyTurn = true;
            text = (curTurn === 'p1' ? 'Your cut' : seatName(curTurn) + '’s cut') + ' — tap any card in the fan to cut the deck (lowest card deals).';
          } else {
            isWait = true;
            text = seatName(curTurn) + ' is cutting the deck…';
          }
        }
      } else if (state.phase === 'cut-starter') {
        const pone = nextSeat(state.dealer);
        const isProg = Boolean(settings && settings.optProgressiveCut && (settings.playerCount || 2) === 2);
        const cutCount = isProg ? Math.max(1, Math.floor((state.handsCompleted || 0) / 2) + 1) : 1;
        const cardPhrase = cutCount > 1 ? `${cutCount} starter cards` : 'the starter card';
        if (pone === me || (!isOnline() && isSeatHuman(pone))) {
          isMyTurn = true;
          text = (pone === 'p1' ? 'You are the pone' : seatName(pone) + ' (pone)') + ` — cut the deck for ${cardPhrase}.`;
        } else {
          isWait = true;
          text = seatName(pone) + ` (pone) is cutting ${cardPhrase} from the deck…`;
        }
      } else if (state.phase === 'cut-reveal') {
        const winnerText = state.dealer === me ? 'You cut lower and deal first!' : (pName(state.dealer) + ' cut lower and deals first!');
        text = 'Cut revealed: ' + winnerText;
      } else if (state.phase === 'dealing') {
        text = (state.dealer === me ? 'You are dealing' : pName(state.dealer) + ' is dealing') + ' the cards…';
      } else if (state.phase === 'play') {
        const cribEl = typeof document !== 'undefined' ? document.getElementById('cribCards') : null;
        if (cribEl) {
          cribEl.classList.remove('counting');
          cribEl.classList.add('playing');
        }
        const activeTurn = state.passingTo || state.turn;
        const isHumanActive = (activeTurn === me) || (!isOnline() && isSeatHuman(activeTurn));
        if (state.frozen) {
          text = state.count === 31 ? 'Thirty-one! Sweeping count…' : 'Go! Sweeping count…';
        } else if (isHumanActive) {
          isMyTurn = true;
          const whoPrefix = (activeTurn === me || activeTurn === 'p1' || activeTurn === 'you') ? 'Your' : (seatName(activeTurn) + '’s');
          text = whoPrefix + ' turn! Play a card (count is ' + state.count + ' / 31).';
        } else {
          isWait = true;
          text = 'Waiting for ' + pName(activeTurn) + ' to play a card… (count is ' + state.count + ' / 31)';
        }
      } else if (state.phase === 'show') {
        const cribEl = typeof document !== 'undefined' ? document.getElementById('cribCards') : null;
        if (cribEl) {
          cribEl.classList.remove('playing');
          cribEl.classList.add('counting');
        }
        if (state.showAct === 'pone') {
          const pone = state.dealer === 'you' ? 'cpu' : 'you';
          text = 'Counting ' + pName(pone) + ' hand (pone)…';
        } else if (state.showAct === 'dealer') {
          text = 'Counting ' + pName(state.dealer) + ' hand (dealer)…';
        } else if (state.showAct === 'crib') {
          text = 'Counting Crib (' + pName(state.dealer) + ')…';
        } else if (state.showAct && state.showAct !== 'done') {
          text = 'Counting ' + pName(state.showAct) + ' hand…';
        } else {
          text = 'Hand scoring complete.';
        }
      }
    }

    if (text && el) {
      let badge = '';
      if (isMyTurn) {
        const activeTurn = (state && (state.passingTo || state.turn));
        const badgeLabel = (activeTurn && activeTurn !== me && activeTurn !== 'p1' && activeTurn !== 'you' && !isOnline() && isSeatHuman(activeTurn))
          ? `${String(seatName(activeTurn)).toUpperCase()} TURN`
          : 'YOUR TURN';
        badge = `<span class="turn-badge your-turn">${badgeLabel}</span> `;
      } else if (isWait) {
        badge = '<span class="turn-badge wait-turn">WAITING</span> ';
      }
      el.innerHTML = badge + '<span>' + text + '</span>';
    }

    return { text, isMyTurn, isWait };
  }

  /**
   * Initializes the draggable table splitter dividing the board and card play area.
   */
  static initTableSplitter(options = {}) {
    if (typeof document === 'undefined') return;
    const splitter = document.getElementById('tableSplitter');
    const splitContainer = document.querySelector('.table.split');
    const leftPanel = document.querySelector('.table-left');
    if (!splitter || !splitContainer || !leftPanel) return;

    const STORAGE_KEY = options.storageKey || 'cc_table_split_width';
    const DEFAULT_WIDTH = options.defaultWidth || '38%';
    const storage = getStorage(options.storage);

    // Load and apply saved preference
    if (storage) {
      try {
        const savedWidth = storage.getItem(STORAGE_KEY);
        if (savedWidth) {
          splitContainer.style.setProperty('--table-left-width', savedWidth);
        }
      } catch (e) {}
    }

    let isDragging = false;
    let startX = 0;
    let startWidthPx = 0;

    const onPointerDown = (e) => {
      if (e.type === 'mousedown' && e.button !== 0) return;
      isDragging = true;
      splitter.classList.add('dragging');
      document.body.classList.add('is-table-resizing');

      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      startX = clientX;
      startWidthPx = leftPanel.getBoundingClientRect().width;

      window.addEventListener('mousemove', onPointerMove, { passive: false });
      window.addEventListener('touchmove', onPointerMove, { passive: false });
      window.addEventListener('mouseup', onPointerUp);
      window.addEventListener('touchend', onPointerUp);
      window.addEventListener('touchcancel', onPointerUp);

      e.preventDefault();
    };

    const onPointerMove = (e) => {
      if (!isDragging) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const deltaX = clientX - startX;

      const containerRect = splitContainer.getBoundingClientRect();
      const containerWidth = containerRect.width;
      if (containerWidth <= 0) return;

      const minLeftPx = 240;
      const maxLeftPx = Math.max(minLeftPx, containerWidth - 280);

      const newLeftPx = Math.max(minLeftPx, Math.min(maxLeftPx, startWidthPx + deltaX));
      const widthPct = Math.round((newLeftPx / containerWidth) * 1000) / 10;
      const clampedPct = Math.max(20, Math.min(75, widthPct));

      const widthVal = `${clampedPct}%`;
      splitContainer.style.setProperty('--table-left-width', widthVal);

      e.preventDefault();
    };

    const onPointerUp = () => {
      if (!isDragging) return;
      isDragging = false;
      splitter.classList.remove('dragging');
      document.body.classList.remove('is-table-resizing');

      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('touchmove', onPointerMove);
      window.removeEventListener('mouseup', onPointerUp);
      window.removeEventListener('touchend', onPointerUp);
      window.removeEventListener('touchcancel', onPointerUp);

      const currentWidth = splitContainer.style.getPropertyValue('--table-left-width');
      if (currentWidth && storage) {
        try {
          storage.setItem(STORAGE_KEY, currentWidth);
        } catch (e) {}
      }
    };

    splitter.addEventListener('mousedown', onPointerDown);
    splitter.addEventListener('touchstart', onPointerDown, { passive: false });

    // Double-click to reset to default
    splitter.addEventListener('dblclick', () => {
      splitContainer.style.setProperty('--table-left-width', DEFAULT_WIDTH);
      if (storage) {
        try {
          storage.removeItem(STORAGE_KEY);
        } catch (e) {}
      }
      const box = document.getElementById('toasts');
      if (box) {
        const el = document.createElement('div');
        el.className = 'toast';
        el.style.borderLeft = '3px solid #f0c64a';
        el.innerHTML = '<div class="why" style="font-size:0.85rem;color:#f8fafc;">Reset cribbage board & playing area layout to default</div>';
        box.prepend(el);
        setTimeout(() => el.remove(), 2500);
      }
    });

    // Keyboard support for accessibility
    splitter.addEventListener('keydown', (e) => {
      const containerRect = splitContainer.getBoundingClientRect();
      const containerWidth = containerRect.width;
      if (containerWidth <= 0) return;

      let currentPct = parseFloat(splitContainer.style.getPropertyValue('--table-left-width')) || 38;
      if (e.key === 'ArrowLeft') {
        currentPct = Math.max(20, currentPct - 2);
        splitContainer.style.setProperty('--table-left-width', `${currentPct}%`);
        if (storage) { try { storage.setItem(STORAGE_KEY, `${currentPct}%`); } catch (err) {} }
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        currentPct = Math.min(75, currentPct + 2);
        splitContainer.style.setProperty('--table-left-width', `${currentPct}%`);
        if (storage) { try { storage.setItem(STORAGE_KEY, `${currentPct}%`); } catch (err) {} }
        e.preventDefault();
      } else if (e.key === 'Enter' || e.key === ' ') {
        splitContainer.style.setProperty('--table-left-width', DEFAULT_WIDTH);
        if (storage) { try { storage.removeItem(STORAGE_KEY); } catch (err) {} }
        e.preventDefault();
      }
    });
  }

  /**
   * Toggles the lobby settings menu or confirms leaving an active game.
   */
  static toggleMenuSettings(fromGame = false, context = {}) {
    if (typeof document === 'undefined') return;
    const menuOptions = document.getElementById('menuOptions');
    const btnSettingsMenu = document.getElementById('btnSettingsMenu');
    const menuVariants = document.getElementById('menuVariants');
    const btnVariantsMenu = document.getElementById('btnVariantsMenu');
    const gameEl = document.getElementById('game');
    const menuEl = document.getElementById('menu');
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const hideModalFn = context.hideModal || (typeof window !== 'undefined' && window.hideModal ? window.hideModal : null);
    const showConfirmModalFn = context.showConfirmModal || (typeof window !== 'undefined' && window.showConfirmModal ? window.showConfirmModal : null);
    const renderRecordBoxFn = context.renderRecordBox || (typeof window !== 'undefined' && window.renderRecordBox ? window.renderRecordBox : null);

    const openSettings = () => {
      if (typeof hideModalFn === 'function') hideModalFn();
      if (gameEl) gameEl.classList.add('hidden');
      if (menuEl) menuEl.classList.remove('hidden');
      if (menuVariants) menuVariants.classList.add('hidden');
      if (btnVariantsMenu) btnVariantsMenu.classList.remove('is-active');
      if (menuOptions) {
        menuOptions.classList.remove('hidden');
        menuOptions.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (btnSettingsMenu) btnSettingsMenu.classList.add('is-active');
      if (typeof renderRecordBoxFn === 'function') renderRecordBoxFn();
    };

    if (fromGame || (gameEl && !gameEl.classList.contains('hidden'))) {
      if (state && state.over) {
        openSettings();
        return;
      }
      if (typeof showConfirmModalFn === 'function') {
        showConfirmModalFn({
          title: 'Go to Game Settings?',
          message: 'Your current match will end and progress will be lost. Are you sure you want to open settings?',
          confirmText: 'Go to Settings',
          cancelText: 'Keep Playing',
          onConfirm: openSettings
        });
      } else {
        openSettings();
      }
      return;
    }

    if (!menuOptions) return;
    if (menuVariants) menuVariants.classList.add('hidden');
    if (btnVariantsMenu) btnVariantsMenu.classList.remove('is-active');

    const isHidden = menuOptions.classList.toggle('hidden');
    const isOpen = !isHidden;

    if (btnSettingsMenu) {
      btnSettingsMenu.classList.toggle('is-active', isOpen);
      const tooltip = isOpen ? 'Hide game settings' : 'Game settings';
      btnSettingsMenu.title = tooltip;
      btnSettingsMenu.setAttribute('aria-label', tooltip);
    }
    if (isOpen) {
      menuOptions.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /**
   * Toggles the variants / board layout menu or confirms leaving an active game.
   * For Cribbage Clash, Pints & Pegs, and Skunked & Stripped, Board Layout directly opens the Track Sandbox.
   */
  static toggleMenuVariants(fromGame = false, context = {}) {
    if (typeof document === 'undefined') return;
    const menuVariants = document.getElementById('menuVariants');
    const btnVariantsMenu = document.getElementById('btnVariantsMenu');
    const menuOptions = document.getElementById('menuOptions');
    const btnSettingsMenu = document.getElementById('btnSettingsMenu');
    const gameEl = document.getElementById('game');
    const menuEl = document.getElementById('menu');
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const hideModalFn = context.hideModal || (typeof window !== 'undefined' && window.hideModal ? window.hideModal : null);
    const showConfirmModalFn = context.showConfirmModal || (typeof window !== 'undefined' && window.showConfirmModal ? window.showConfirmModal : null);
    const renderRecordBoxFn = context.renderRecordBox || (typeof window !== 'undefined' && window.renderRecordBox ? window.renderRecordBox : null);

    const brand = (typeof window !== 'undefined' && typeof window.detectBrand === 'function')
      ? window.detectBrand()
      : (context.brand || (typeof window !== 'undefined' && (window.currentBrand || window.currentLogoBrand)) || 'crazy');
    const isCrazy = brand === 'crazy';

    // When clicking Board Layout in Clash, Pints, or Adult, open or toggle the track sandbox
    if (!isCrazy) {
      const isSandboxOpen = (typeof window !== 'undefined' && window.ClashSandboxModal && window.ClashSandboxModal.isOpen) ||
                            (typeof ClashSandboxModal !== 'undefined' && ClashSandboxModal.isOpen);
      if (isSandboxOpen) {
        if (typeof window !== 'undefined' && window.ClashSandboxModal && typeof window.ClashSandboxModal.close === 'function') {
          window.ClashSandboxModal.close();
        } else if (typeof ClashSandboxModal !== 'undefined' && typeof ClashSandboxModal.close === 'function') {
          ClashSandboxModal.close();
        }
        return;
      }

      UIController.hideMenuVariants();
      UIController.hideMenuOptions();
      const sandboxContext = { brand, state, settings };

      if (typeof window !== 'undefined') {
        if (brand === 'pints' && typeof window.openPintsSandbox === 'function') {
          return window.openPintsSandbox(sandboxContext);
        }
        if (brand === 'adult' && typeof window.openAdultSandbox === 'function') {
          return window.openAdultSandbox(sandboxContext);
        }
        if (typeof window.openClashSandbox === 'function') {
          return window.openClashSandbox(sandboxContext);
        }
        if (window.ClashSandboxModal && typeof window.ClashSandboxModal.open === 'function') {
          return window.ClashSandboxModal.open(sandboxContext);
        }
      }
      if (typeof ClashSandboxModal !== 'undefined' && typeof ClashSandboxModal.open === 'function') {
        return ClashSandboxModal.open(sandboxContext);
      }
      return;
    }

    const openVariants = () => {
      if (typeof hideModalFn === 'function') hideModalFn();
      if (gameEl) gameEl.classList.add('hidden');
      if (menuEl) menuEl.classList.remove('hidden');
      if (menuOptions) menuOptions.classList.add('hidden');
      if (typeof document !== 'undefined') {
        if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
          document.exitFullscreen().catch(() => {});
        }
        if (document.body) {
          document.body.classList.remove('is-3d-fullscreen');
        }
      }
      const r3d = (typeof window !== 'undefined') ? (window._currentRenderer3D || window.boardRenderer3D) : null;
      if (r3d && typeof r3d.exitFullScreen === 'function') {
        r3d.exitFullScreen();
      }
      if (menuVariants) {
        menuVariants.classList.remove('hidden');
        menuVariants.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (btnVariantsMenu) btnVariantsMenu.classList.add('is-active');
      if (typeof renderRecordBoxFn === 'function') renderRecordBoxFn();
    };

    if (fromGame || (gameEl && !gameEl.classList.contains('hidden'))) {
      if (state && state.over) {
        openVariants();
        return;
      }
      const label = 'Crazy Variants';
      if (typeof showConfirmModalFn === 'function') {
        showConfirmModalFn({
          title: `Go to ${label}?`,
          message: `Your current match will end and progress will be lost. Are you sure you want to open ${label}?`,
          confirmText: `Go to ${label}`,
          cancelText: 'Keep Playing',
          onConfirm: openVariants
        });
      } else {
        openVariants();
      }
      return;
    }

    if (!menuVariants) return;
    if (menuOptions) menuOptions.classList.add('hidden');
    if (btnSettingsMenu) btnSettingsMenu.classList.remove('is-active');

    const isHidden = menuVariants.classList.toggle('hidden');
    const isOpen = !isHidden;

    if (isOpen) {
      if (typeof document !== 'undefined') {
        if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
          document.exitFullscreen().catch(() => {});
        }
        if (document.body) {
          document.body.classList.remove('is-3d-fullscreen');
        }
      }
      const r3d = (typeof window !== 'undefined') ? (window._currentRenderer3D || window.boardRenderer3D) : null;
      if (r3d && typeof r3d.exitFullScreen === 'function') {
        r3d.exitFullScreen();
      }
      menuVariants.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (btnVariantsMenu) {
      btnVariantsMenu.classList.toggle('is-active', isOpen);
      const name = 'Crazy Variants';
      const tooltip = isOpen ? `Hide ${name}` : name;
      btnVariantsMenu.title = tooltip;
      btnVariantsMenu.setAttribute('aria-label', tooltip);
    }
  }

  /**
   * Hides the lobby settings menu and variants menu.
   */
  static hideMenuOptions() {
    if (typeof document === 'undefined') return;
    const menuOptions = document.getElementById('menuOptions');
    if (menuOptions) menuOptions.classList.add('hidden');
    const btnSettingsMenu = document.getElementById('btnSettingsMenu');
    if (btnSettingsMenu) btnSettingsMenu.classList.remove('is-active');
    UIController.hideMenuVariants();
  }

  /**
   * Hides the lobby variants / board layout menu.
   */
  static hideMenuVariants() {
    if (typeof document === 'undefined') return;
    const menuVariants = document.getElementById('menuVariants');
    if (menuVariants) menuVariants.classList.add('hidden');
    const btnVariantsMenu = document.getElementById('btnVariantsMenu');
    if (btnVariantsMenu) btnVariantsMenu.classList.remove('is-active');
  }

  toggleMenuSettings(fromGame, context) {
    return UIController.toggleMenuSettings(fromGame, context);
  }

  toggleMenuVariants(fromGame, context) {
    return UIController.toggleMenuVariants(fromGame, context);
  }

  hideMenuOptions() {
    return UIController.hideMenuOptions();
  }

  hideMenuVariants() {
    return UIController.hideMenuVariants();
  }

  static showStartGameModal(context = {}) {
    if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showStartGameModal === 'function') {
      return window.modalManager.showStartGameModal(context);
    }
    const overlay = document.getElementById('startGameOverlay');
    if (overlay) overlay.style.display = 'flex';
  }

  static hideStartGameModal() {
    if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.hideStartGameModal === 'function') {
      return window.modalManager.hideStartGameModal();
    }
    const overlay = document.getElementById('startGameOverlay');
    if (overlay) overlay.style.display = 'none';
  }

  showStartGameModal(context) {
    return UIController.showStartGameModal(context);
  }

  hideStartGameModal() {
    return UIController.hideStartGameModal();
  }

  static showPlayerOfflineModal(seatId, seatName, onConfirm, onWait, onCancel, context = {}) {
    if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showPlayerOfflineModal === 'function') {
      return window.modalManager.showPlayerOfflineModal(seatId, seatName, onConfirm, onWait, onCancel, context);
    }
    const mgr = CC?.ModalManager || (typeof ModalManager !== 'undefined' ? ModalManager : null);
    if (mgr && typeof mgr.showPlayerOfflineModal === 'function') {
      return mgr.showPlayerOfflineModal(seatId, seatName, onConfirm, onWait, onCancel, context);
    }
  }

  showPlayerOfflineModal(seatId, seatName, onConfirm, onWait, onCancel, context) {
    return UIController.showPlayerOfflineModal(seatId, seatName, onConfirm, onWait, onCancel, context);
  }

  static cancelGameAndReturnToMenu() {
    if (typeof window !== 'undefined' && typeof window.cancelGameAndReturnToMenu === 'function') {
      return window.cancelGameAndReturnToMenu();
    }
  }

  cancelGameAndReturnToMenu() {
    return UIController.cancelGameAndReturnToMenu();
  }

  /**
   * Initializes theme toggle buttons across navbar and menu headers.
   * @param {Object} [context]
   */
  static initThemeButtons(context = {}) {
    if (typeof document === 'undefined') return;
    const applyTheme = context.applyTheme || (typeof window !== 'undefined' ? window.applyTheme : () => {});
    const toggleTheme = context.toggleTheme || (typeof window !== 'undefined' ? window.toggleTheme : () => {});

    if (typeof window !== 'undefined') window.isThemeReady = true;
    applyTheme();

    const b = document.getElementById('btnTheme');
    if (b) b.onclick = toggleTheme;

    const btnMenu = document.getElementById('btnThemeMenu');
    if (btnMenu) btnMenu.onclick = (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      toggleTheme();
    };

    const btnHeader = document.getElementById('btnThemeHeader');
    if (btnHeader) btnHeader.onclick = (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      toggleTheme();
    };
  }

  /**
   * Initializes sound toggle button with local storage persistence.
   * @param {Object} [context]
   */
  static initSoundButton(context = {}) {
    if (typeof document === 'undefined') return;
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : { on: true, ensure: () => {}, peg: () => {} });
    const b = document.getElementById('btnSound');
    if (!b) return;

    const sync = () => { b.textContent = sfx.on ? 'Sound on' : 'Sound off'; };
    sync();

    b.onclick = () => {
      sfx.on = !sfx.on;
      try {
        const s = getStorage();
        if (s) {
          s.setItem('crazyCribbage.sfx', sfx.on ? 'on' : 'off');
        }
      } catch (e) {}
      if (sfx.on) {
        if (sfx.ensure) sfx.ensure();
        if (sfx.peg) sfx.peg();
      }
      sync();
    };
  }

  /**
   * Updates is-mobile-device CSS classes based on viewport dimensions and touch capabilities.
   */
  static updateMobileDeviceClass() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    try {
      const isNarrow = window.innerWidth <= 920;
      const isShort = window.innerHeight <= 520 && window.innerWidth <= 1024;
      const isMobileUA = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '');
      const isTouchNarrow = (('ontouchstart' in window) || (navigator.maxTouchPoints > 0)) && window.innerWidth <= 1024;
      const isMobile = isNarrow || isShort || isMobileUA || isTouchNarrow;

      if (isMobile) {
        document.documentElement.classList.add('is-mobile-device');
        if (document.body) document.body.classList.add('is-mobile-device');
      } else {
        document.documentElement.classList.remove('is-mobile-device');
        if (document.body) document.body.classList.remove('is-mobile-device');
      }
    } catch (e) {
      // ignore
    }
  }

  /**
   * Updates seat display labels and title headers based on player count, game mode, and brand rules.
   * @param {Object} [context]
   */
  static applySeatLabels(context = {}) {
    if (typeof document === 'undefined') return;

    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const getPlayerCount = context.getPlayerCount || (typeof window !== 'undefined' ? window.getPlayerCount : () => 2);
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const getAppBrand = context.getAppBrand || (typeof window !== 'undefined' ? window.getAppBrand : () => 'crazy');
    const isSoloVsPairs = context.isSoloVsPairs || (typeof window !== 'undefined' ? window.isSoloVsPairs : () => false);
    const pName = context.pName || (typeof window !== 'undefined' ? window.pName : (s) => s);
    const isHvH = context.isHvH || (typeof window !== 'undefined' ? window.isHvH : () => false);

    const y = document.getElementById('youLabel');
    const c = document.getElementById('cpuLabel');
    const yt = document.getElementById('youTitle');
    const ct = document.getElementById('cpuTitle');
    const btnCam = document.getElementById('btnCamToggle');

    if (btnCam) {
      btnCam.classList.toggle('hidden', !isOnline());
    }

    try {
      const isMultiHuman = isOnline() || (typeof isHvH === 'function' ? isHvH() : false);
      const chatMgr = (context.ChatManager && context.ChatManager.getInstance ? context.ChatManager.getInstance() : ChatManager.getInstance());
      if (chatMgr) {
        if (isMultiHuman) {
          chatMgr.renderChatWidget(context);
          chatMgr.setChatVisibility(true, context);
        } else {
          chatMgr.setChatVisibility(false, context);
        }
      }
    } catch (e) {}

    if (isOnline()) {
      const me = mySeat();
      const count = getPlayerCount();
      if (count === 2) {
        const other = me === 'p1' ? 'p2' : 'p1';
        if (y) y.textContent = `${seatName(me)} (${me.toUpperCase()})`;
        if (c) c.textContent = `${seatName(other)} (${other.toUpperCase()})`;
        if (yt) yt.textContent = 'Your hand';
        if (ct) ct.textContent = `${seatName(other)}’s hand`;
      } else {
        if (y) y.textContent = `${seatName(me)} (${me.toUpperCase()})`;
        if (yt) yt.textContent = `Your hand (${me.toUpperCase()})`;
      }
      return;
    }

    const pCount = settings ? (parseInt(settings.playerCount, 10) || 2) : 2;
    const brand = typeof getAppBrand === 'function' ? getAppBrand() : 'crazy';
    const GameOptionsEngine = (typeof window !== 'undefined' && window.GameOptionsEngine) ? window.GameOptionsEngine : null;
    const allowCpu = GameOptionsEngine && typeof GameOptionsEngine.isCpuAllowed === 'function'
      ? GameOptionsEngine.isCpuAllowed(brand)
      : !['pints', 'adult'].includes(brand);

    if (isSoloVsPairs()) {
      if (y) y.textContent = pName('team1');
      if (c) c.textContent = pName('team2');
    } else {
      if (y) y.textContent = pName('p1');
      if (c) c.textContent = (!allowCpu && (pName('p2') === 'Computer' || pName('p2') === 'cpu')) ? 'Player 2' : pName('p2');
    }
    const is2p = !settings || pCount === 2;
    if (yt) yt.textContent = is2p ? ((isHvH() || !allowCpu) ? 'Player 1' : 'Your hand') : 'Your hand (P1)';
    if (ct) ct.textContent = is2p ? ((isHvH() || !allowCpu) ? 'Player 2' : 'Computer') : (seatName('p2'));
  }

  /**
   * Shows the discard/play wait overlay with a custom message and disables discard button.
   * @param {string} [msg]
   */
  static showWait(msg) {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('waitOverlay');
    const t = document.getElementById('waitMsg');
    if (t && msg) t.textContent = msg;
    if (el) el.classList.add('on');
    const btn = document.getElementById('btnDiscard');
    if (btn) btn.disabled = true;
  }

  /**
   * Hides the discard/play wait overlay and enables discard button.
   */
  static hideWait() {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('waitOverlay');
    if (el) el.classList.remove('on');
    const btn = document.getElementById('btnDiscard');
    if (btn) btn.disabled = false;
  }

  initThemeButtons(context) {
    return UIController.initThemeButtons(context);
  }

  initSoundButton(context) {
    return UIController.initSoundButton(context);
  }

  updateMobileDeviceClass() {
    return UIController.updateMobileDeviceClass();
  }

  applySeatLabels(context) {
    return UIController.applySeatLabels(context);
  }

  showWait(msg) {
    return UIController.showWait(msg);
  }

  /**
   * Appends an event log message to the on-screen game log.
   * @param {string} msg
   */
  static log(msg) {
    if (typeof document === 'undefined' || !msg) return;
    const el = document.getElementById('log');
    if (!el) return;
    const p = document.createElement('p');
    if (UIController.isBonusMessage(msg)) {
      p.classList.add('log-bonus');
    }
    p.textContent = msg;
    el.prepend(p);
  }

  /**
   * Sequentially announces score combinations with staggered callout banners.
   * @param {Array} pts - List of scored point combinations ({ why, n })
   * @param {Object} [context]
   */
  static announceCombos(pts, context = {}) {
    if (!pts || !pts.length) return;
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : UIController.fancyCall);
    const fancyPegPhrase = context.fancyPegPhrase || (typeof window !== 'undefined' ? window.fancyPegPhrase : UIController.fancyPegPhrase);
    const isBonusMessage = context.isBonusMessage || (typeof window !== 'undefined' ? window.isBonusMessage : UIController.isBonusMessage);

    let cumDelay = 0;
    pts.forEach((p) => {
      const isBonus = isBonusMessage(p.why);
      const ms = isBonus ? 2200 : 1300;
      setTimeout(() => fancyCall(fancyPegPhrase(p.why, p.n), 'combo', ms), cumDelay);
      cumDelay += (isBonus ? 2250 : 1350);
    });
  }

  log(msg) {
    return UIController.log(msg);
  }

  fancyCall(text, kind, ms) {
    return UIController.fancyCall(text, kind, ms);
  }

  announceCombos(pts, context) {
    return UIController.announceCombos(pts, context);
  }
}


